import * as React from "react";
import { UitkIcon } from "uitk-react-icons";
import { CollapsableContainerProps } from "./typings";
import { useLocalization } from "@shared-ui/localization-context";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "../../../utility/analytics/FlexAnalyticsUtils";
import { AnalyticsStore } from "bernie-core";
import { withStores } from "../../../../stores";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutFlexItem, UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkText } from "uitk-react-text";

const getTrackingConfig = (isExpanded: boolean, moduleName: string): FlexTrackingInfo => {
  const key = isExpanded ? "open" : "close";

  return {
    moduleName,
    action: Action.CLICK,
    linkName: `${key} collapsed wizard`,
    rfrr: `collapsed-wizard.${key}`,
  };
};

export enum CollapsableComponentName {
  STOREFRONT_WIZARD_REGION = "StorefrontWizardRegion",
}

export const CollapsableContainer = withStores("analytics")((props: CollapsableContainerProps) => {
  const useExpando = (defaultState: boolean, componentName: CollapsableComponentName, analytics?: AnalyticsStore) => {
    const [isExpandedWizard, setIsExpandedWizard] = React.useState(defaultState);

    const toggleCallback = React.useCallback(
      () =>
        setIsExpandedWizard((isExpandedState) => {
          const nextState = !isExpandedState;
          const trackInfo = getTrackingConfig(nextState, componentName);

          sendDelayedTrackEvent(trackInfo, analytics);

          return nextState;
        }),
      [setIsExpandedWizard, analytics]
    );

    return {
      toggle: toggleCallback,
      isExpanded: isExpandedWizard,
    };
  };

  const { toggle, isExpanded } = useExpando(false, props.componentName, props.analytics);

  const { formatText } = useLocalization();

  if (!props.isCollapsedWizardEnabled) {
    return <>{props.children}</>;
  }

  return (
    <div className="CollapsedWizard collapsed-wizard" data-testid="collapsed-wizard">
      <div onClick={toggle} className="uitk-link all-y-padding-three" data-testid="collapsed-wizard-toggle">
        <UitkLayoutFlex>
          <UitkLayoutFlexItem>
            <UitkSpacing margin="three">
              <UitkIcon id="playback-icon" name="search" />
            </UitkSpacing>
          </UitkLayoutFlexItem>
          <UitkLayoutFlex wrap="wrap">
            <UitkLayoutFlexItem minWidth="full_width">
              <div data-testid="collapsed-wizard-title">
                <UitkText size={500} weight="medium" overflow="truncate">
                  {formatText("wizard.collapsed.title")}
                </UitkText>
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <div data-testid="collapsed-wizard-subtitle">
                <UitkText size={200} weight="regular" overflow="truncate">
                  {formatText("wizard.collapsed.subtitle")}
                </UitkText>
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkLayoutFlex>
      </div>
      {isExpanded && <>{props.children}</>}
    </div>
  );
});
